<script>
// Orquestrador da navegação 2026: escolhe a sidebar (gestão ou cliente) pelo
// contexto no store `navigation`, centraliza o roteamento e mantém o estado
// recolhido. Sincroniza o contexto inicial no load (rota de cliente + cliente
// selecionado => contexto cliente).
import {
  CLIENTE_GROUPS,
  CLIENTE_FIRST_ROUTE,
  GESTAO_GATEWAY_ROUTE,
  isClienteHiddenRoute,
} from '@modules/navegacao-v2/constants/navigation-modules'
import { NAV_CONTEXT } from '@modules/navegacao-v2/store/state'
import { routeModuleKey } from '@modules/navegacao-v2/utils/visible-modules'
import { readStoredContext } from '@modules/navegacao-v2/utils/context-storage'
import { resolveDynamicRoute } from '@modules/navegacao-v2/utils/resolve-dynamic-route'
import { shouldOpenClienteModal } from '@modules/navegacao-v2/utils/cliente-modal'
import { resolveModuleEntranceCliente } from '@modules/navegacao-v2/utils/module-entrance'
import { applyClienteEntrance as applyClienteModuleEntrance } from '@modules/navegacao-v2/utils/cliente-module-entrance'
import GestaoSidebar from './gestao-sidebar.vue'
import ClienteSidebar from './cliente-sidebar.vue'

// Chaves de módulo do contexto cliente (1º segmento da rota), para casar mesmo
// após redirects (ex: 'carteira.minha.cliente' => 'carteira').
const CLIENTE_MODULE_KEYS = new Set(
  CLIENTE_GROUPS.flatMap((g) => g.items).map((i) => routeModuleKey(i.routeName))
)

export default {
  name: 'NavegacaoV2Sidebar',
  components: { GestaoSidebar, ClienteSidebar },
  props: {
    collapsed: { type: Boolean, default: false },
    mobile: { type: Boolean, default: false },
    open: { type: Boolean, default: false },
  },
  data() {
    return {
      // Guarda contra clique duplo enquanto a requisição de status de lead
      // (crm-cliente-lead-status) está em andamento — ver onSelect.
      loadingClienteModal: false,
      // Token de geração da entrada em curso — ver applyClienteEntrance.
      entranceToken: 0,
    }
  },
  computed: {
    context() {
      return this.$store.getters['navigation/context']
    },
    isCliente() {
      return this.context === NAV_CONTEXT.CLIENTE
    },
  },
  mounted() {
    // Sincroniza o contexto só no load/reload (não a cada navegação) — entrar num
    // cliente é explícito via navigation/enterClient, e assim módulos compartilhados
    // (ex: Consolidador na gestão) não trocam de contexto ao serem clicados.
    this.syncContextFromRoute()
  },
  methods: {
    syncContextFromRoute(route = this.$route) {
      // "Visão por cliente" é conceito de PROFISSIONAL entrando na visão de
      // UM cliente — nunca se aplica a um cliente final logado. Sem este
      // guard, F5 numa rota como carteira.minha (o pouso do próprio cliente,
      // e que também está em CLIENTE_GROUPS) promove o contexto pra CLIENTE
      // e mostra o catálogo inteiro (Carteira, WS, Cobranças, FP, Compliance,
      // CRM, Drive, Backoffice...) — nenhum deles pensado pro cliente final,
      // só filtrados por feature flag do escritório, nunca por IS_CLIENTE.
      const user = this.$currentUser()
      if (user.IS_CLIENTE) {
        this.applyContext(NAV_CONTEXT.GESTAO)
        return
      }
      const client = this.$currentClient()
      const hasClient = !!(client && client.id)
      const storedContext = readStoredContext({ userId: user.id_interno })
      if (storedContext) {
        this.applyStoredContext({ storedContext, hasClient })
        return
      }
      this.applyRouteHeuristic({ route, hasClient })
    },
    applyRouteHeuristic({ route, hasClient }) {
      // Exclui rotas agregadas/gestão (CLIENTE_HIDDEN_ROUTES) do match por
      // módulo — senão um F5 em ex. crm.overview com cliente cacheado
      // reclassifica indevidamente o contexto pra CLIENTE só por
      // compartilhar módulo com crm.relationship.
      const routeName = route ? route.name : ''
      const inClientRoute =
        CLIENTE_MODULE_KEYS.has(routeModuleKey(routeName)) &&
        !isClienteHiddenRoute(routeName)
      if (inClientRoute) {
        this.applyContext(hasClient ? NAV_CONTEXT.CLIENTE : NAV_CONTEXT.GESTAO)
        return
      }
      this.decideContext(NAV_CONTEXT.GESTAO)
    },
    applyStoredContext({ storedContext, hasClient }) {
      const isOrphanCliente =
        storedContext === NAV_CONTEXT.CLIENTE && !hasClient
      if (isOrphanCliente) {
        this.applyContext(NAV_CONTEXT.GESTAO)
        return
      }
      this.applyContext(storedContext)
    },
    decideContext(context) {
      this.$store.dispatch('navigation/setContext', context)
    },
    applyContext(context) {
      this.$store.dispatch('navigation/applyContext', context)
    },
    toggle() {
      this.$emit('toggle')
    },
    go(name, params) {
      if (!name || (this.$route && this.$route.name === name))
        return Promise.resolve()
      // push() é monkey-patched no router (swallowNavigationFailure) — engole só
      // NavigationFailure/ChunkLoadError e re-lança erros reais para o Sentry.
      // Por isso NÃO encadeamos .catch aqui (silenciaria erros legítimos). Retorna
      // a promise: applyClienteEntrance precisa saber quando a navegação assenta
      // antes de abrir o modal (ver comentário lá).
      return this.$router.push(params ? { name, params } : { name })
    },
    async onSelect(item) {
      // Itens externos (Market News, Suporte) abrem em nova janela — VERBATIM da nav antiga.
      if (item && item.external) {
        window.open(item.external, '_blank')
        return
      }
      // Contexto cliente: itens marcados com clienteModal delegam a decisão de
      // rota + modal para o resolver do PRÓPRIO módulo (item.clienteEntranceModule,
      // registrado via registerClienteEntranceResolver — ex.: @modules/crm/index.js).
      // A sidebar não conhece CRM/Leads/Relacionamento; só invoca pela chave.
      // Sem cliente ativo / em upsell, o helper retorna false e caímos na navegação
      // normal (que usa clienteSkipTo como fallback).
      //
      // NÃO checamos isClienteModalHostReady() aqui antes de navegar — alguns
      // módulos (ex.: Compliance) só montam o host do modal DENTRO da própria
      // rota (compliance-nav-bar.vue), então na primeira navegação o host nunca
      // vai estar pronto ANTES de navegar (ovo-e-galinha: precisamos entrar na
      // rota pra montar o host, mas a checagem prévia bloquearia essa entrada).
      // Bug real observado: modal do Compliance nunca abria no primeiro clique
      // porque essa trava vinha antes da navegação. applyClienteEntrance já
      // espera o host ficar pronto DEPOIS de navegar (waitClienteModalHostReady,
      // com timeout de segurança) — isso já cobre tanto módulos com host
      // "aquecido" antecipadamente (CRM, via navegacao-v2-shell.vue) quanto
      // módulos com host frio (Compliance).
      // Marca o início desta seleção ANTES de qualquer await — se o usuário
      // clicar em outro item (mesmo módulo ou não) antes desta entrada
      // terminar, o clique novo incrementa entranceToken e invalida esta
      // chamada (ver applyClienteEntrance). Cobre também o próprio
      // resolveModuleEntranceCliente abaixo, que já é assíncrono.
      const token = ++this.entranceToken
      const client = this.$currentClient()
      if (shouldOpenClienteModal(item, { isCliente: this.isCliente, client })) {
        if (await this.tryClienteEntrance(item, client, token)) return
        // Sem resolver registrado para este módulo (ou ele devolveu null): cai no
        // fallback de navegação normal abaixo, igual a um item sem clienteModal.
      }
      // Resolve destino dinâmico (Comissões/Tesouraria/Consolidador) com a MESMA
      // lógica da nav antiga; itens estáticos caem direto no routeName.
      const routeName = resolveDynamicRoute(item, {
        access: this.$store.getters['parameters/show'] || {},
        currentUser: this.$currentUser(),
        isCliente: this.isCliente,
      })
      this.go(routeName)
    },
    // Consulta o resolver do módulo (item.clienteEntranceModule) e, se ele
    // devolver uma entrada válida, aplica (navega + abre modal). Devolve
    // true quando onSelect deve parar aqui (clique duplo bloqueado, ou
    // entrada resolvida/aplicada) — false quando não há resolver registrado
    // ou ele devolveu null, caindo no fallback de navegação normal.
    async tryClienteEntrance(item, client, token) {
      if (this.loadingClienteModal) return true
      this.loadingClienteModal = true
      const resolved = await resolveModuleEntranceCliente(
        item.clienteEntranceModule,
        { client, item }
      )
      this.loadingClienteModal = false
      if (!resolved) return false
      await this.applyClienteEntrance(resolved, item.clienteEntranceModule, token)
      return true
    },
    // Executa o resultado do resolver do módulo (navegar → aguardar o host do
    // modal → emitir). O miolo vive em utils/cliente-module-entrance.js,
    // compartilhado com a busca global; aqui só injetamos a navegação da
    // sidebar e o entranceToken (ver onSelect) como critério de seleção
    // obsoleta — o motivo de cada espera está documentado no util.
    async applyClienteEntrance(resolved, moduleKey, token) {
      await applyClienteModuleEntrance(resolved || {}, {
        moduleKey,
        go: (...args) => this.go(...args),
        isStale: () => token !== this.entranceToken,
      })
    },
    onEnterClientes() {
      this.decideContext(NAV_CONTEXT.GESTAO)
      this.go(GESTAO_GATEWAY_ROUTE)
    },
    onPicked() {
      // O switcher/picker já trocou o cliente e o contexto (enterClient).
      this.go(CLIENTE_FIRST_ROUTE)
    },
    onBack() {
      this.go(GESTAO_GATEWAY_ROUTE)
    },
    onOpenAll() {
      this.decideContext(NAV_CONTEXT.GESTAO)
      this.go(GESTAO_GATEWAY_ROUTE)
    },
  },
}
</script>

<template>
  <ClienteSidebar
    v-if="isCliente"
    :collapsed="collapsed"
    :mobile="mobile"
    :open="open"
    @toggle="toggle"
    @select="onSelect"
    @back="onBack"
    @picked="onPicked"
    @open-all="onOpenAll"
  />
  <GestaoSidebar
    v-else
    :collapsed="collapsed"
    :mobile="mobile"
    :open="open"
    @toggle="toggle"
    @select="onSelect"
    @enter-clientes="onEnterClientes"
  />
</template>
